import type { Props as FloaterType } from 'react-floater';
import type { Step } from 'react-joyride';
import { Box, List, Text } from '@ksyos/design-system';
import type { DecisionAidPlacement } from '@frontend/components/elements/DecisionAid';
import { ServiceCode } from '@common/enums';
import type { IService } from '../home/newProcess/newProcessInterfaces';

interface GgzConsultationDecisionAidStepProps {
    examples: string[];
    feedbackText: string;
    shortText: string;
}

function GgzConsultationDecisionAidStep({ shortText, examples, feedbackText }: GgzConsultationDecisionAidStepProps) {
    return (
        <Box as="dl">
            <Box
                display="grid"
                gap={3}
                gridTemplateColumns={['1fr', 'repeat(4,minmax(0,1fr))']}
            >
                <Text
                    as="dt"
                    fontWeight="medium"
                    size="sm"
                >
                    In het kort
                </Text>
                <Text
                    as="dd"
                    size="sm"
                    sx={{ gridColumn: ['auto / auto', '2 / span 3'] }}
                >
                    {shortText}
                </Text>

                <Text
                    as="dt"
                    fontWeight="medium"
                    size="sm"
                >
                    Bijvoorbeeld
                </Text>
                <Text
                    as="dd"
                    size="sm"
                    sx={{ gridColumn: ['auto / auto', '2 / span 3'] }}
                >
                    <List
                        size="small"
                        styleType="bullet"
                    >
                        {examples.map((example, index) => (
                            <List.Item key={index}>{example}</List.Item>
                        ))}
                    </List>
                </Text>

                <Text
                    as="dt"
                    fontWeight="medium"
                    size="sm"
                >
                    Terugkoppeling
                </Text>
                <Text
                    as="dd"
                    size="sm"
                    sx={{ gridColumn: ['auto / auto', '2 / span 3'] }}
                >
                    {feedbackText}
                </Text>
            </Box>
        </Box>
    );
}

export function GgzConsultationDecisionAidSteps(
    services: IService[],
    placement?: DecisionAidPlacement,
    selectedServiceId?: string
): Step[] {
    return services.map((service) => {
        const stepProps = getStepContent(service.code);

        return {
            target: `.automation-processFlowService-${service.code}`,
            title: service.name,
            content: stepProps ? <GgzConsultationDecisionAidStep {...stepProps} /> : undefined,
            disableBeacon: true,
            placement: placement ? placement : 'bottom',
            floaterProps: {
                disableAnimation: true,
                disableFlip: true,
            } as FloaterType,
        };
    });
}

function getStepContent(serviceCode: ServiceCode): GgzConsultationDecisionAidStepProps | null {
    switch (serviceCode) {
        case ServiceCode.GGZCg:
            return {
                shortText:
                    'Dit consult leent zich goed om een specialist een gerichte, korte vraag te stellen die weinig context behoeft.',
                examples: [
                    'Patiënt heeft veel last van bijwerkingen van citalopram. Wat adviseren jullie?',
                    'Patiënt heeft positieve ervaringen met EMDR. Zou dit zinvol zijn in deze nieuwe situatie?',
                    'Wat is het advies voor het afbouwen van quetiapine?',
                ],
                feedbackText: 'Binnen 7 dagen ontvang je hier een geschreven antwoord van een specialist.',
            };

        case ServiceCode.GGZCt:
            return {
                shortText:
                    'Wil je graag telefonisch overleggen met een specialist? Kies dan voor dit consult. Zo kun je meer context geven bij een casus.',
                examples: [
                    'De vooruitgang van de patiënt stokt. Wat zou ik verder kunnen doen binnen de huisartsenprakijk?',
                    'Graag wil ik telefonisch overleggen over het al dan niet verwijzen naar de GGZ',
                    'Graag wil ik telefonisch overleggen over de behandeling die ik alvast in kan zetten tijdens de overbrugging naar de GGZ',
                ],
                feedbackText:
                    'Het telefonisch overleg vindt binnen een week plaats. Hierna ontvang je een schriftelijke terugkoppeling in je takenlijst.',
            };

        case ServiceCode.GGZCf2f:
            return {
                shortText:
                    'De patiënt wordt eenmalig fysiek gezien door de specialist voor een zo volledig mogelijk antwoord op je uitgebreide of complexe vragen.',
                examples: [
                    'Er is sprake van diverse terugkerende klachten. Het verzoek om mee te kijken of een behandeling binnen de GGZ gewenst is.',
                    'Deel je mijn vermoeden van ASS? Denk je dat het zin heeft om verdere diagnostiek in gang te zetten?',
                    'Patiënt met schizofrenie slikt al jaren antipsychotica en is stabiel. Graag beoordelen of de medicatie afgebouwd kan worden.',
                ],
                feedbackText:
                    'Na het consult maakt de specialist een gespreksverslag. Dit verslag ontvang je gemiddeld binnen 3 weken na de consultaanvraag.',
            };
        case ServiceCode.GGZCmdo:
            return {
                shortText:
                    'In het MDO bespreek je met een specialist in een uur een aantal patiënten. Dien hier per patiënt een aanvraag in voor een MDO.',
                examples: [
                    'De vooruitgang stokt. Wat zou ik verder kunnen doen binnen de huisartsenpraktijk?',
                    'Graag wil ik overleggen over het al dan niet verwijzen naar de GGZ',
                    'Graag wil ik overleggen over de opties voor een patiënt die tussen wal en schip valt',
                ],
                feedbackText:
                    'Samen met de specialist plan je een moment in voor het MDO consult. Het inplannen hiervan gebeurt niet binnen Ksyos.',
            };

        case ServiceCode.GGZCtriage:
            return {
                shortText:
                    'De patiënt wordt gescreend door Embloom middels een TeleScreen vragenlijst en een telefonische afspraak met een triagepsycholoog.',
                examples: [
                    'Er is sprake van angst en somberheidsklachten. Graag jullie triagering voor het echelon-advies en behandelrichting.',
                    'Er speelt veel bij deze patiënt. Welke problematiek is voorliggend?',
                    'Is er sprake van onderliggende ontwikkelings- of persoonlijkheidspathologie? En welke invloed heeft dit op het echelonadvies?',
                ],
                feedbackText:
                    'Binnen 3 weken ontvang je een uitgebreid adviesrapport van Embloom. In dit adviesrapport vind je de uitslag van de TeleScreen vragenlijst en de bevindingen/aanbevelingen van de triagepsycholoog.',
            };

        default:
            return null;
    }
}
